// react and js
import PropTypes from "prop-types";
import JsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// mui

// components
import addFontNormal from "./OpenSans-normal";
import addFontBold from "./OpenSans-bold";
import backendRequest from "../BackendRequest";
import i18n from "../../i18n";

// utils
import { findEmailsInText, isMexicanCountry } from "../../utils/common";

// hooks

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function numberWithCommas(x) {
  return parseFloat(x)
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function topline(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  let startX = 57;
  const startY = startYParam;
  let endX = doc.internal.pageSize.width - startX;
  const spaceBetweenWords = 8;

  // Underline
  doc.setDrawColor(71, 74, 76);
  doc.setLineWidth(0.5);
  doc.line(startX, startY + lineSpacing / 3, endX, startY + lineSpacing / 3);

  // Left aligned items
  doc.setFontSize(fontSizes.NormalFontSize);
  const itemsLeft = Object.values(dataStructure.topline.left);
  itemsLeft.forEach((text) => {
    if (text) {
      doc.text(text, startX, startY);
      startX =
        startX +
        doc.getStringUnitWidth(text) * fontSizes.NormalFontSize +
        spaceBetweenWords;
    }
  });

  // Right aligned items
  let text = null;
  if (dataStructure.custom.customReceiverVatNumber !== "") {
    text = `${dataStructure.topline.right.email}, ${dataStructure.custom.customSenderPhone}`;
  } else {
    text = dataStructure.topline.right.email;
  }
  doc.setFontSize(fontSizes.NormalFontSize);
  if (text) {
    doc.text(text, endX, startY, "right");
    endX =
      endX -
      doc.getStringUnitWidth(text) * fontSizes.NormalFontSize -
      spaceBetweenWords;
  }

  // Return new y coordinate
  return startY;
}

function recipent(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  const startX = 57;
  let startY = startYParam;

  // Recipent
  doc.setFontSize(fontSizes.SubTitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);

  const lines = dataStructure.recipent;
  if (
    dataStructure.custom.customReceiverVatNumber !== "" ||
    dataStructure.custom.customReceiverRegistryCode !== ""
  ) {
    lines.placeholder = " ";
  }

  if (dataStructure.custom.customReceiverVatNumber !== "") {
    lines.customReceiverVatNumber = i18n.t("components.accountingDownloads.vatId", {
      value: dataStructure.custom.customReceiverVatNumber,
    });
  }

  if (dataStructure.custom.customReceiverRegistryCode !== "") {
    lines.customReceiverRegistryCode = i18n.t("components.accountingDownloads.registryCode", {
      value: dataStructure.custom.customReceiverRegistryCode,
    });
  }

  const recipentObject = Object.values(lines);
  recipentObject.forEach((recipentLine) => {
    if (recipentLine) {
      doc.text(recipentLine, startX, (startY += lineSpacing));
    }
  });

  // Return new y coordinate
  return startY;
}

function heading(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  let startX = 57;
  let startY = startYParam;
  const endX = doc.internal.pageSize.width - startX;
  const spaceBetweenWords = 3;

  // Invoice no.
  startY = 273;
  doc.setFontSize(fontSizes.SubTitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.number, startX, startY);
  //
  startX =
    startX +
    doc.getStringUnitWidth(dataStructure.labels.number) *
      fontSizes.SubTitleFontSize +
    spaceBetweenWords;
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.meta.number, startX, startY);

  // Date
  startX =
    endX -
    doc.getStringUnitWidth(dataStructure.meta.date) *
      fontSizes.SubTitleFontSize -
    spaceBetweenWords;
  doc.setFontSize(fontSizes.SubTitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.date, startX, startY, "right");
  //
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.meta.date, endX, startY, "right");

  // Custom message top
  if (dataStructure.custom.customMessageTop !== "") {
    startY += lineSpacing * 2;

    const messageTop = Object.values(dataStructure.custom.customMessageTop);
    messageTop.forEach((text) => {
      if (text) {
        startX = 57;
        startY += lineSpacing * 1;
        doc.setFontSize(fontSizes.NormalFontSize);
        doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
        doc.text(text, startX, startY);
      }
    });
  }

  // Title
  startX = 57;
  if (dataStructure.custom.customCustomLineItems === "") {
    startY += lineSpacing * 4;
  } else {
    startY += lineSpacing * 2;
  }
  doc.setFontSize(fontSizes.TitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.for, startX, (startY += lineSpacing + 2));

  // Add pay online button
  if (
    dataStructure.custom.customReceipeDate === null &&
    dataStructure.custom.customOnlinePayable !== ""
  ) {
    // Create rectangle
    doc.setFillColor(245, 195, 66);
    doc.rect(endX - 113, startY - 14, 113, 20, "F");

    // Create text
    doc.setFontSize(fontSizes.SubTitleFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
    doc.text(i18n.t("components.accountingDownloads.payOnline"), endX - 28, startY, "right");

    // Create link box
    doc.link(endX - 113, startY - 14, 113, 20, {
      url: `${process.env.REACT_APP_URL_API_EVENT_LISTENER}?request_type=pay_invoice_online&billing_id=${dataStructure.meta.number}&online_payable=${dataStructure.custom.customOnlinePayable}&dashboard_forward=1`,
    });
  }

  // Underline
  startX = 57;
  startY += lineSpacing;
  doc.setDrawColor(76, 184, 51);
  doc.setLineWidth(0.5);
  doc.line(startX, startY, endX, startY);

  // Return new y coordinate
  return startY;
}

// A note's lead-in label ("Clientes en Mexico:") is set bold. Found
// structurally rather than by matching the wording, so it keeps working across
// translations; the length cap keeps a colon in the middle of a sentence from
// being mistaken for a label.
const NOTE_LABEL_MAX_LENGTH = 40;

// The spans of a note that are set bold: the lead-in label, plus every email
// address, which additionally becomes a mailto link. Sorted by position so the
// paragraph renderer can walk them in one pass.
function noteEmphasis(text) {
  const spans = [];

  const labelEnd = text.indexOf(":");
  if (labelEnd !== -1 && labelEnd < NOTE_LABEL_MAX_LENGTH) {
    spans.push({ start: 0, end: labelEnd + 1, url: null });
  }

  findEmailsInText(text).forEach((email) => {
    spans.push({
      start: email.index,
      end: email.index + email.value.length,
      url: `mailto:${email.value}`,
    });
  });

  return spans.sort((a, b) => a.start - b.start);
}

// Draws a wrapped paragraph in which the given spans are set bold, and spans
// carrying a url also get a link annotation laid over them. This is the
// project's link style (bold, no underline) carried into the PDF, where the
// green hover state has no equivalent.
function paragraphWithEmphasis(
  doc,
  text,
  spans,
  startX,
  startYParam,
  maxWidth,
  lineSpacing
) {
  let startY = startYParam;

  // Bold is wider than the regular weight splitTextToSize measures with, so
  // reserve that difference up front — an emphasised span can then never push
  // its line past the right margin.
  let boldOverhang = 0;
  spans.forEach((span) => {
    const phrase = text.slice(span.start, span.end);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
    const boldWidth = doc.getTextWidth(phrase);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
    boldOverhang += boldWidth - doc.getTextWidth(phrase);
  });

  const lines = doc.splitTextToSize(text, maxWidth - boldOverhang);

  let lineSearchFrom = 0;
  lines.forEach((line) => {
    // Where this wrapped line sits in the source text, so the spans (which are
    // offsets into that text) can be mapped onto it.
    const lineStart = text.indexOf(line, lineSearchFrom);

    if (lineStart === -1) {
      // Wrapping returned something that isn't a verbatim slice of the source
      // (an unbreakable word being hyphenated, say) — no reliable offsets, so
      // fall back to plain regular-weight output for this line.
      doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
      doc.text(line, startX, startY);
      startY += lineSpacing;
      return;
    }

    lineSearchFrom = lineStart + line.length;

    let cursorX = startX;
    let drawnUpTo = 0;

    const drawChunk = (from, to, bold, url) => {
      const chunk = line.slice(from, to);
      if (chunk === "") {
        return;
      }

      doc.setFont(
        doc.vars.fontFamily,
        bold ? doc.vars.fontWeightBold : doc.vars.fontWeightNormal
      );
      doc.text(chunk, cursorX, startY);

      const chunkWidth = doc.getTextWidth(chunk);
      if (url) {
        const fontSize = doc.getFontSize();
        doc.link(cursorX, startY - fontSize, chunkWidth, fontSize + 3, { url });
      }
      cursorX += chunkWidth;
    };

    spans.forEach((span) => {
      // Clamped to this line, so a span straddling a line break renders on
      // both of them.
      const from = Math.max(span.start - lineStart, 0);
      const to = Math.min(span.end - lineStart, line.length);
      if (from >= to) {
        return;
      }

      drawChunk(drawnUpTo, from, false, null);
      drawChunk(from, to, true, span.url);
      drawnUpTo = to;
    });

    drawChunk(drawnUpTo, line.length, false, null);
    startY += lineSpacing;
  });

  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);

  // Return new y coordinate
  return startY;
}

function itemTable(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  const startX = 57;
  let startY = startYParam;
  const endX = doc.internal.pageSize.width - startX;
  const colum2StartX = 386;
  const colum3StartX = 460;

  // Head
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  startY += lineSpacing * 1.5;
  doc.text(dataStructure.labels.tableItems, startX, startY);
  doc.text(dataStructure.labels.tableQty, colum2StartX, startY, {
    align: "right",
  });
  doc.text(dataStructure.labels.tableSinglePrice, colum3StartX, startY, {
    align: "right",
  });
  doc.text(dataStructure.labels.tableSingleTotal, endX, startY, {
    align: "right",
  });

  // Underline
  startY += lineSpacing;
  doc.setDrawColor(76, 184, 51);
  doc.setLineWidth(0.5);
  doc.line(startX, startY, endX, startY);

  if (dataStructure.custom.customCustomLineItems === "") {
    // Item title
    startY += lineSpacing * 1.5;
    doc.setFontSize(fontSizes.NormalFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
    doc.text(dataStructure.item.title, startX, startY);

    // Item description
    startY += doc.internal.getLineHeight();
    doc.setFontSize(fontSizes.NormalFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
    const splitDescription = doc.splitTextToSize(
      dataStructure.item.description,
      colum2StartX - startX - lineSpacing * 1.5
    );
    doc.text(splitDescription, startX, startY);
    doc.text(dataStructure.item.qty, colum2StartX, startY, { align: "right" });
    doc.text(dataStructure.item.amount, colum3StartX, startY, {
      align: "right",
    });
    doc.text(dataStructure.item.total, endX, startY, { align: "right" });
    startY += splitDescription.length * doc.internal.getLineHeight();
  } else {
    dataStructure.custom.customCustomLineItems.forEach(
      (customCustomLineItem, index) => {
        // Item title
        if (index === 0) {
          startY += lineSpacing * 1.5;
        } else {
          startY += lineSpacing * 0.5;
        }
        doc.setFontSize(fontSizes.NormalFontSize);
        doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
        doc.text(customCustomLineItem.item_bold, startX, startY);

        // Item description
        startY += doc.internal.getLineHeight();
        doc.setFontSize(fontSizes.NormalFontSize);
        doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
        const splitDescription = doc.splitTextToSize(
          customCustomLineItem.item_normal,
          colum2StartX - startX - lineSpacing * 1.5
        );
        doc.text(String(splitDescription), startX, startY);
        doc.text(String(customCustomLineItem.quantity), colum2StartX, startY, {
          align: "right",
        });
        doc.text(
          `${numberWithCommas(customCustomLineItem.price)} ${
            dataStructure.custom.customSenderCurrencyConverted
          }`,
          colum3StartX,
          startY,
          {
            align: "right",
          }
        );
        doc.text(
          `${numberWithCommas(customCustomLineItem.amount)} ${
            dataStructure.custom.customSenderCurrencyConverted
          }`,
          endX,
          startY,
          {
            align: "right",
          }
        );
        startY += splitDescription.length * doc.internal.getLineHeight();
      }
    );
  }

  // Return new y coordinate
  return startY;
}

function sumUp(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  let startX = 57;
  let startY = startYParam;
  const endX = doc.internal.pageSize.width - startX;

  // Underline
  doc.setDrawColor(76, 184, 51);
  doc.setLineWidth(1);
  doc.line(startX, startY, endX, startY);

  // Sub-total
  startY += lineSpacing * 2;
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.totalSub, 460, startY, "right");
  //
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.meta.totalSub, endX, startY, "right");

  // Vat
  startY += lineSpacing * 2;
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.totalVat, 460, startY, "right");
  //
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.meta.totalVat, endX, startY, "right");

  // Total
  startY += lineSpacing * 2;
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(dataStructure.labels.totalTotal, 460, startY, "right");
  //
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.meta.totalTotal, endX, startY, "right");

  // Underlines
  startX =
    endX -
    doc.getStringUnitWidth(dataStructure.meta.totalTotal) *
      fontSizes.NormalFontSize -
    5;
  doc.setLineWidth(0.5);
  startY += 4;
  doc.line(startX - 1, startY, endX + 1, startY);
  startY += 2;
  doc.line(startX - 2, startY, endX + 2, startY);

  // Return new y coordinate
  return startY;
}

function message(doc, dataStructure, startYParam, fontSizes, lineSpacing) {
  // Coordinates
  const startX = 57;
  let startY = startYParam;

  if (dataStructure.custom.customReceipeDate !== null) {
    // Message A
    startY += lineSpacing * 1;
    doc.setFontSize(fontSizes.NormalFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
    const splitTextA = doc.splitTextToSize(dataStructure.meta.messageA, 350);
    splitTextA.forEach((textLine) => {
      doc.text(textLine, startX, startY);
    });
  }

  // Custom message bottom
  if (dataStructure.custom.customMessageBottom !== "") {
    startY += lineSpacing * 1;

    const messageBottom = Object.values(
      dataStructure.custom.customMessageBottom
    );
    messageBottom.forEach((text) => {
      if (text) {
        startY += lineSpacing * 1;
        doc.setFontSize(fontSizes.NormalFontSize);
        doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
        doc.text(text, startX, startY);
      }
    });
  }

  // Message B
  startY += lineSpacing * 2;
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  const splitTextB = doc.splitTextToSize(dataStructure.meta.messageB, 350);
  splitTextB.forEach((textLine) => {
    doc.text(textLine, startX, startY);
    startY += lineSpacing;
  });

  // Message Mexico (CFDI request window). Set across the full content width
  // instead of the 350pt message column so the notice stays compact and does
  // not run into the footer.
  if (isMexicanCountry(dataStructure.custom.customReceiverCountry)) {
    startY += lineSpacing;
    doc.setFontSize(fontSizes.NormalFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);

    const messageMexico = dataStructure.meta.messageMexicoCfdi;
    startY = paragraphWithEmphasis(
      doc,
      messageMexico,
      noteEmphasis(messageMexico),
      startX,
      startY,
      doc.internal.pageSize.width - startX * 2,
      lineSpacing
    );
  }

  // Return new y coordinate
  return startY;
}

function footerItem(
  doc,
  items,
  headline,
  startX,
  startYParam,
  fontSizes,
  lineSpacing
) {
  // Coordinates
  const padding = lineSpacing / 2;
  const startY = startYParam;
  let tempY = startY;

  // Headline
  doc.setFontSize(fontSizes.SmallFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(headline, startX + padding, (tempY += lineSpacing));
  tempY += lineSpacing / 3;

  // Items
  doc.setFontSize(fontSizes.SmallFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  const itemsObject = Object.values(items);
  itemsObject.forEach((item) => {
    if (item) {
      doc.text(item, startX + padding, (tempY += lineSpacing));
    }
  });

  // Vertical seperator
  doc.setDrawColor(71, 74, 76);
  doc.setLineWidth(0.5);
  doc.line(startX + 1, startY + padding, startX + 1, tempY + padding / 3);

  // Return new y coordinate
  return startY;
}

function timeTable(doc, dataStructure, startYParam, fontSizes) {
  // Coordinates
  const startX = 57;

  // 1st topline
  let startY = 160;
  doc.setFontSize(fontSizes.TitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(i18n.t("components.accountingDownloads.timeTable"), startX, startY);

  // 2n topline
  startY = 180;
  doc.setFontSize(fontSizes.SubTitleFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.text(dataStructure.custom.customTimeTableData[0][0], startX, startY);

  const dodyData = [];
  dataStructure.custom.customTimeTableData.forEach((element, index) => {
    if (
      index >= 2 &&
      index < dataStructure.custom.customTimeTableData.length - 1
    ) {
      const elementModified = element;
      elementModified[3] = elementModified[3].replaceAll("<br>", "\n");
      dodyData.push(elementModified);
    }
  });

  // Timetable
  doc.setFontSize(fontSizes.SmallFontSize);
  autoTable(doc, {
    startY: 195,
    headStyles: {
      lineWidth: 1,
      lineColor: [71, 74, 76],
      fillColor: [241, 155, 55],
      fontSize: 9,
      cellPadding: { horizontal: 5, vertical: 2 },
    },
    footStyles: {
      lineWidth: 1,
      lineColor: [71, 74, 76],
      fillColor: [248, 237, 206],
      fontSize: 9,
      cellPadding: { horizontal: 5, vertical: 2 },
    },
    styles: {
      lineWidth: 1,
      lineColor: [71, 74, 76],
      textColor: [0, 0, 0],
      fontSize: 9,
      cellPadding: { horizontal: 5, vertical: 2 },
    },
    theme: "plain",
    head: [dataStructure.custom.customTimeTableData[1]],
    body: dodyData,
    foot: [
      dataStructure.custom.customTimeTableData[
        dataStructure.custom.customTimeTableData.length - 1
      ],
    ],
  });

  // Return new y coordinate
  return startY;
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
function DownloadDocument(documentData) {
  DownloadDocument.propTypes = {
    documentData: PropTypes.any.isRequired,
  };

  // ----------------------------------------------------------------------
  // Input data structures
  // ----------------------------------------------------------------------
  /* eslint-disable no-unused-vars */
  const [
    billingId,
    dashboardId,
    invoiceDate,
    receipeDate,
    billingAggreementId,
    transactionId,
    paymentMethod,
    product,
    netAmount,
    taxAmount,
    totalAmount,
    totalAmountAlt,
    toName,
    toEmail,
    toCountry,
    toProvince,
    toCity,
    toPostalCode,
    toStreet,
    toOptionalLine,
    senderEmail,
    senderOrganization,
    senderCountry,
    senderProvince,
    senderCity,
    senderPostalCode,
    senderStreet,
    senderOptionalLine,
    senderPhone,
    senderCurrency,
    senderRegistryCode,
    senderVatNumber,
    senderVatRate,
    senderVatName,
    senderDueToDays,
    externalInvoiceId,
    latestKnownLocation,
    receiverVatNumber,
    receiverRegistryCode,
    timeTableData,
    customLineItems,
    paymentDetails,
    messageTop,
    messageBottom,
    onlinePayable,
  ] = documentData;
  /* eslint-enable no-unused-vars */

  const senderCurrencyConverted = senderCurrency.replace("EUR", "€");

  let timeTableDataJsonized = "";
  if (timeTableData !== "") {
    timeTableDataJsonized = JSON.parse(timeTableData);
  }
  let customLineItemsJsonized = "";
  if (customLineItems !== "") {
    customLineItemsJsonized = JSON.parse(customLineItems);
  }
  let paymentDetailsJsonized = "";
  if (paymentDetails !== "") {
    paymentDetailsJsonized = JSON.parse(paymentDetails);
  }
  let messageTopJsonized = "";
  if (messageTop !== "") {
    messageTopJsonized = JSON.parse(messageTop);
  }

  let messageBottomJsonized = "";
  if (messageBottom !== "") {
    messageBottomJsonized = JSON.parse(messageBottom);
  }

  // ----------------------------------------------------------------------
  // Data structure config
  // ----------------------------------------------------------------------
  const selectedLabels = {
    number: i18n.t("components.accountingDownloads.invoiceId"),
    date: i18n.t("components.accountingDownloads.date"),
    for:
      dashboardId !== "0"
        ? i18n.t("components.accountingDownloads.invoiceForUser", { id: dashboardId })
        : i18n.t("components.accountingDownloads.invoice"),
    tableItems: i18n.t("components.accountingDownloads.items"),
    tableQty: i18n.t("components.accountingDownloads.qty"),
    tableSinglePrice: i18n.t("components.accountingDownloads.price"),
    tableSingleTotal: i18n.t("components.accountingDownloads.amount"),
    totalSub: i18n.t("components.accountingDownloads.subtotal"),
    totalVat: i18n.t("components.accountingDownloads.vatLine", {
      name: senderVatName,
      rate: senderVatRate,
    }),
    totalTotal: i18n.t("components.accountingDownloads.total"),
    footerContact: i18n.t("components.accountingDownloads.contact"),
    footerPaymentDetails:
      paymentDetailsJsonized === ""
        ? i18n.t("components.accountingDownloads.paymentServiceDetails")
        : i18n.t("components.accountingDownloads.accountDetails"),
    footerLegalDetails: i18n.t("components.accountingDownloads.registration"),
  };

  const selectedMeta = {
    number: externalInvoiceId === "" ? billingId : externalInvoiceId,
    date: invoiceDate.slice(0, 10),
    totalSub: `${numberWithCommas(netAmount)} ${senderCurrencyConverted}`,
    totalVat: `${numberWithCommas(taxAmount)} ${senderCurrencyConverted}`,
    totalTotal: `${numberWithCommas(totalAmount)} ${senderCurrencyConverted}`,
    messageA: i18n.t("components.accountingDownloads.amountPayed"),
    messageB: i18n.t("components.accountingDownloads.thankYou"),
    messageMexicoCfdi: i18n.t("components.accountingDownloads.mexicoCfdiNote"),
  };

  let recipentPostalCodeCity = "";
  if (toPostalCode !== "" && toCity !== "") {
    recipentPostalCodeCity = `${toPostalCode}, ${toCity}`;
  } else if (toPostalCode !== "") {
    recipentPostalCodeCity = `${toPostalCode}`;
  } else if (toCity !== "") {
    recipentPostalCodeCity = `${toCity}`;
  }

  let recipentCountryProvince = "";
  if (toCountry !== "" && toProvince !== "") {
    recipentCountryProvince = `${toCountry}, ${toProvince}`;
  } else if (toCountry !== "") {
    recipentCountryProvince = `${toCountry}`;
  } else if (toProvince !== "") {
    recipentCountryProvince = `${toProvince}`;
  }

  let senderPostalCodeCity = "";
  if (senderPostalCode !== "" && senderCity !== "") {
    senderPostalCodeCity = `${senderPostalCode}, ${senderCity}`;
  } else if (senderPostalCode !== "") {
    senderPostalCodeCity = `${senderPostalCode}`;
  } else if (senderCity !== "") {
    senderPostalCodeCity = `${senderCity}`;
  }

  let senderCountryProvince = "";
  if (senderCountry !== "" && senderProvince !== "") {
    senderCountryProvince = `${senderCountry}, ${senderProvince}`;
  } else if (senderCountry !== "") {
    senderCountryProvince = `${senderCountry}`;
  } else if (senderProvince !== "") {
    senderCountryProvince = `${senderProvince}`;
  }

  const dataStructure = {
    labels: selectedLabels,
    topline: {
      // Maped
      left: {
        organization: senderOrganization,
      },
      // Maped
      right: {
        email: senderEmail,
      },
    },

    recipent: {
      // Mapped
      organization: "",
      name: toName,
      street: toStreet,
      optionaleLine: toOptionalLine,
      postalCodeCity: recipentPostalCodeCity,
      countryProvince: recipentCountryProvince,
      email: toEmail,
    },

    meta: selectedMeta,

    item: {
      title: i18n.t("components.accountingDownloads.subscription"),
      description: product,
      amount: `${numberWithCommas(netAmount)} ${senderCurrencyConverted}`,
      qty: "1",
      total: `${numberWithCommas(netAmount)} ${senderCurrencyConverted}`,
    },

    footer: {
      contact: {
        organization: senderOrganization,
        street: senderStreet,
        optionaleLine: senderOptionalLine,
        postalCodeCity: senderPostalCodeCity,
        countryProvince: senderCountryProvince,
        email: senderEmail,
      },
      paymentDetails: {
        method: paymentMethod,
        billingAgreement: billingAggreementId,
        transaction: transactionId,
      },
      LegalDetails: {
        vatNumber: i18n.t("components.accountingDownloads.vatId", { value: senderVatNumber }),
        registryCode: i18n.t("components.accountingDownloads.registryCode", { value: senderRegistryCode }),
      },
      page: {
        total: timeTableDataJsonized === "" ? "1" : "2",
      },
    },

    custom: {
      customSenderCurrencyConverted: senderCurrencyConverted,
      customReceipeDate: receipeDate,
      customTotalAmountAlt: totalAmountAlt,
      customSenderPhone: senderPhone,
      customSenderDueToDays: senderDueToDays,
      customExternalInvoiceId: externalInvoiceId,
      customLatestKnownLocation: latestKnownLocation,
      customReceiverVatNumber: receiverVatNumber,
      customReceiverRegistryCode: receiverRegistryCode,
      customReceiverCountry: toCountry,
      customTimeTableData: timeTableDataJsonized,
      customCustomLineItems: customLineItemsJsonized,
      customPaymentDetails: paymentDetailsJsonized,
      customMessageTop: messageTopJsonized,
      customMessageBottom: messageBottomJsonized,
      customOnlinePayable: onlinePayable,
    },
  };

  // ----------------------------------------------------------------------
  // Add font
  // ----------------------------------------------------------------------
  addFontNormal();
  addFontBold();

  // ----------------------------------------------------------------------
  // Doc object and basic config
  // ----------------------------------------------------------------------
  const doc = new JsPDF("p", "pt");
  doc.vars = {};
  // How to convert fonts: https://www.devlinpeck.com/content/jspdf-custom-font
  doc.vars.fontFamily = "OpenSans";
  doc.vars.fontWeightBold = "bold";
  doc.vars.fontWeightNormal = "normal";
  doc.setFont(doc.vars.fontFamily);

  const lineSpacing = 12;
  const fontSizes = {
    TitleFontSize: 14,
    SubTitleFontSize: 12,
    NormalFontSize: 10,
    SmallFontSize: 9,
  };

  // ----------------------------------------------------------------------
  // Coordinates
  // ----------------------------------------------------------------------
  let startY = 130;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let startX = 57;
  const endX = pageWidth - startX;
  const thirdX = (pageWidth - startX * 2) / 3;

  // ----------------------------------------------------------------------
  // Brand mark (icon + business name) — matches the dashboard header style.
  // ----------------------------------------------------------------------
  const brandIcon = new Image();
  brandIcon.src = "/icon/icon-512-512.png";
  const brandIconSize = 30;
  const brandGap = 6;
  const brandFontSize = 16.2;
  const brandY = 25;
  doc.setFontSize(brandFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  const brandName = process.env.REACT_APP_NAME_BIZ;
  const brandTextWidth = doc.getTextWidth(brandName);
  const brandBlockWidth = brandIconSize + brandGap + brandTextWidth;
  const brandX = pageWidth / 2 - brandBlockWidth / 2;
  doc.addImage(brandIcon, "png", brandX, brandY, brandIconSize, brandIconSize);
  // Re-assert bold + stroke the glyphs to fake a heavier weight (OpenSans
  // only ships Regular + Bold, so this gives the brand mark extra heft).
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
  doc.setFontSize(brandFontSize);
  doc.setTextColor(46, 46, 46);
  doc.setDrawColor(46, 46, 46);
  doc.setLineWidth(0.4);
  doc.text(
    brandName,
    brandX + brandIconSize + brandGap,
    brandY + brandIconSize / 2,
    { baseline: "middle", renderingMode: "fillThenStroke" }
  );
  doc.setTextColor(0, 0, 0);
  doc.link(brandX, brandY, brandBlockWidth, brandIconSize, {
    url: process.env.REACT_APP_URL_FRONTEND_WEBSITE,
  });

  // ----------------------------------------------------------------------
  // Sender
  // ----------------------------------------------------------------------
  startY = topline(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Receiver
  // ----------------------------------------------------------------------
  startY += 10;
  startY = recipent(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Heading
  // ----------------------------------------------------------------------
  startY = heading(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Item table
  // ----------------------------------------------------------------------
  startY = itemTable(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Sum up
  // ----------------------------------------------------------------------
  startY = sumUp(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Message
  // ----------------------------------------------------------------------
  startY = message(doc, dataStructure, startY, fontSizes, lineSpacing);

  // ----------------------------------------------------------------------
  // Footer seperation line
  // ----------------------------------------------------------------------
  startY = pageHeight - 120;
  doc.setDrawColor(71, 74, 76);
  doc.setLineWidth(1);
  doc.line(startX, startY, endX, startY);

  // ----------------------------------------------------------------------
  // Contact details
  // ----------------------------------------------------------------------
  startY += lineSpacing / 3;
  footerItem(
    doc,
    dataStructure.footer.contact,
    dataStructure.labels.footerContact,
    startX,
    startY,
    fontSizes,
    lineSpacing
  );

  // ----------------------------------------------------------------------
  // Payment details
  // ----------------------------------------------------------------------
  if (paymentDetailsJsonized === "") {
    startX += thirdX + 15;
    footerItem(
      doc,
      dataStructure.footer.paymentDetails,
      dataStructure.labels.footerPaymentDetails,
      startX,
      startY,
      fontSizes,
      lineSpacing
    );
  } else {
    startX += thirdX + 15;
    footerItem(
      doc,
      paymentDetailsJsonized,
      dataStructure.labels.footerPaymentDetails,
      startX,
      startY,
      fontSizes,
      lineSpacing
    );
  }

  // ----------------------------------------------------------------------
  // Tax details
  // ----------------------------------------------------------------------
  startX += thirdX + 20;
  footerItem(
    doc,
    dataStructure.footer.LegalDetails,
    dataStructure.labels.footerLegalDetails,
    startX,
    startY,
    fontSizes,
    lineSpacing
  );

  // ----------------------------------------------------------------------
  // Page number
  // ----------------------------------------------------------------------
  startY = pageHeight - 14;
  doc.setFontSize(fontSizes.NormalFontSize);
  doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
  doc.text(
    `1 / ${dataStructure.footer.page.total}`,
    pageWidth / 2 -
      (doc.getStringUnitWidth(`1 / ${dataStructure.footer.page.total}`) *
        fontSizes.NormalFontSize) /
        2,
    startY
  );

  if (timeTableDataJsonized !== "") {
    // ----------------------------------------------------------------------
    // Second page
    // ----------------------------------------------------------------------
    doc.addPage();

    // ----------------------------------------------------------------------
    // Coordinates
    // ----------------------------------------------------------------------
    startY = 130;
    startX = 57;

    // ----------------------------------------------------------------------
    // Brand mark (icon + business name) — matches the dashboard header style.
    // ----------------------------------------------------------------------
    doc.addImage(brandIcon, "png", brandX, brandY, brandIconSize, brandIconSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightBold);
    doc.setFontSize(brandFontSize);
    doc.setTextColor(46, 46, 46);
    doc.setDrawColor(46, 46, 46);
    doc.setLineWidth(0.4);
    doc.text(
      brandName,
      brandX + brandIconSize + brandGap,
      brandY + brandIconSize / 2,
      { baseline: "middle", renderingMode: "fillThenStroke" }
    );
    doc.setTextColor(0, 0, 0);
    doc.link(brandX, brandY, brandBlockWidth, brandIconSize, {
      url: process.env.REACT_APP_URL_FRONTEND_WEBSITE,
    });

    // ----------------------------------------------------------------------
    // Sender
    // ----------------------------------------------------------------------
    startY = topline(doc, dataStructure, startY, fontSizes, lineSpacing);

    // ----------------------------------------------------------------------
    // Timetable
    // ----------------------------------------------------------------------
    startY = timeTable(doc, dataStructure, startY, fontSizes);

    // ----------------------------------------------------------------------
    // Footer seperation line
    // ----------------------------------------------------------------------
    startY = pageHeight - 120;
    doc.setDrawColor(71, 74, 76);
    doc.setLineWidth(1);
    doc.line(startX, startY, endX, startY);

    // ----------------------------------------------------------------------
    // Contact details
    // ----------------------------------------------------------------------
    startY += lineSpacing / 3;
    footerItem(
      doc,
      dataStructure.footer.contact,
      dataStructure.labels.footerContact,
      startX,
      startY,
      fontSizes,
      lineSpacing
    );

    // ----------------------------------------------------------------------
    // Payment details
    // ----------------------------------------------------------------------
    if (paymentDetailsJsonized === "") {
      startX += thirdX + 15;
      footerItem(
        doc,
        dataStructure.footer.paymentDetails,
        dataStructure.labels.footerPaymentDetails,
        startX,
        startY,
        fontSizes,
        lineSpacing
      );
    } else {
      startX += thirdX + 15;
      footerItem(
        doc,
        paymentDetailsJsonized,
        dataStructure.labels.footerPaymentDetails,
        startX,
        startY,
        fontSizes,
        lineSpacing
      );
    }

    // ----------------------------------------------------------------------
    // Tax details
    // ----------------------------------------------------------------------
    startX += thirdX + 20;
    footerItem(
      doc,
      dataStructure.footer.LegalDetails,
      dataStructure.labels.footerLegalDetails,
      startX,
      startY,
      fontSizes,
      lineSpacing
    );

    // ----------------------------------------------------------------------
    // Page number
    // ----------------------------------------------------------------------
    startY = pageHeight - 14;
    doc.setFontSize(fontSizes.NormalFontSize);
    doc.setFont(doc.vars.fontFamily, doc.vars.fontWeightNormal);
    doc.text(
      `2 / ${dataStructure.footer.page.total}`,
      pageWidth / 2 -
        (doc.getStringUnitWidth(`2 / ${dataStructure.footer.page.total}`) *
          fontSizes.NormalFontSize) /
          2,
      startY
    );
  }

  // ----------------------------------------------------------------------
  // Download
  // ----------------------------------------------------------------------
  if (externalInvoiceId === "") {
    doc.save(
      `${invoiceDate
        .slice(0, 10)
        .replaceAll("-", "_")}_invoice_${billingId}.pdf`
    );
  } else {
    doc.save(
      `${invoiceDate
        .slice(0, 10)
        .replaceAll("-", "_")}_invoice_${externalInvoiceId}.pdf`
    );
  }
}

function DownloadInvoice(invoiceData) {
  DownloadInvoice.propTypes = {
    invoiceData: PropTypes.any.isRequired,
  };

  // ----------------------------------------------------------------------
  // Input data structures
  // ----------------------------------------------------------------------
  /* eslint-disable no-unused-vars */
  const [
    billingId,
    dashboardId,
    invoiceDate,
    receipeDate,
    billingAggreementId,
    transactionId,
    paymentMethod,
    product,
    netAmount,
    taxAmount,
    totalAmount,
    totalAmountAlt,
    toName,
    toEmail,
    toCountry,
    toProvince,
    toCity,
    toPostalCode,
    toStreet,
    toOptionalLine,
    senderEmail,
    senderOrganization,
    senderCountry,
    senderProvince,
    senderCity,
    senderPostalCode,
    senderStreet,
    senderOptionalLine,
    senderPhone,
    senderCurrency,
    senderRegistryCode,
    senderVatNumber,
    senderVatRate,
    senderVatName,
    senderDueToDays,
    externalInvoiceId,
    latestKnownLocation,
    receiverVatNumber,
    receiverRegistryCode,
    timeTableData,
    customLineItems,
    paymentDetails,
    messageTop,
    messageBottom,
    passPhrase1,
    passPhrase2,
    passPhrase3,
    passPhrase4,
    passPhrase5,
  ] = invoiceData;
  /* eslint-enable no-unused-vars */

  if (
    passPhrase1 === "2146560ef5d518512c53ecb5caebe289" &&
    passPhrase2 === "75db178026ae7ce09f00b2ce6a99a62c" &&
    passPhrase3 === "9b948cde05822ee0eb7b5922976ed9b2" &&
    passPhrase4 === "9f2867705fdf152dccfeb0eaa1f18609" &&
    passPhrase5 === "639d199155d5022ae44227e31f3aa6d2"
  ) {
    const sendData = {
      request_type: "dashboard",
      route_info: "invoice_printer",
      operation: "data_lookup",
      data_context: {
        billing_id: billingId,
        pass_phrase_1: passPhrase1,
        pass_phrase_2: passPhrase2,
        pass_phrase_3: passPhrase3,
        pass_phrase_4: passPhrase4,
        pass_phrase_5: passPhrase5,
      },
    };

    backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData).then(
      (contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("message");

        if (checkKey === true) {
          if (contentFromBackend.message === "success") {
            // Modify invoice data
            const modifiedInvoiceData = invoiceData;
            modifiedInvoiceData[0] = contentFromBackend.billing_id;
            modifiedInvoiceData[1] = contentFromBackend.dashboard_id;
            modifiedInvoiceData[2] = contentFromBackend.invoice_date;
            modifiedInvoiceData[3] = contentFromBackend.receipe_date;
            modifiedInvoiceData[4] = contentFromBackend.billing_aggreement_id;
            modifiedInvoiceData[5] = contentFromBackend.transaction_id;
            modifiedInvoiceData[6] = contentFromBackend.payment_method;
            modifiedInvoiceData[7] = contentFromBackend.product;
            modifiedInvoiceData[8] = contentFromBackend.net_amount;
            modifiedInvoiceData[9] = contentFromBackend.tax_amount;
            modifiedInvoiceData[10] = contentFromBackend.total_amount;
            modifiedInvoiceData[11] = contentFromBackend.total_amount_alt;
            modifiedInvoiceData[12] = contentFromBackend.to_name;
            modifiedInvoiceData[13] = contentFromBackend.to_email;
            modifiedInvoiceData[14] = contentFromBackend.to_country;
            modifiedInvoiceData[15] = contentFromBackend.to_province;
            modifiedInvoiceData[16] = contentFromBackend.to_city;
            modifiedInvoiceData[17] = contentFromBackend.to_postal_code;
            modifiedInvoiceData[18] = contentFromBackend.to_street;
            modifiedInvoiceData[19] = contentFromBackend.to_opional_line;
            modifiedInvoiceData[20] = contentFromBackend.sender_email;
            modifiedInvoiceData[21] = contentFromBackend.sender_organization;
            modifiedInvoiceData[22] = contentFromBackend.sender_country;
            modifiedInvoiceData[23] = contentFromBackend.sender_province;
            modifiedInvoiceData[24] = contentFromBackend.sender_city;
            modifiedInvoiceData[25] = contentFromBackend.sender_postal_code;
            modifiedInvoiceData[26] = contentFromBackend.sender_street;
            modifiedInvoiceData[27] = contentFromBackend.sender_opional_line;
            modifiedInvoiceData[28] = contentFromBackend.sender_phone;
            modifiedInvoiceData[29] = contentFromBackend.sender_currency;
            modifiedInvoiceData[30] = contentFromBackend.sender_registry_code;
            modifiedInvoiceData[31] = contentFromBackend.sender_vat_number;
            modifiedInvoiceData[32] = contentFromBackend.sender_vat_rate;
            modifiedInvoiceData[33] = contentFromBackend.sender_vat_name;
            modifiedInvoiceData[34] = contentFromBackend.sender_due_to_days;
            modifiedInvoiceData[35] = contentFromBackend.external_invoice_id;
            modifiedInvoiceData[36] = contentFromBackend.latest_known_location;
            modifiedInvoiceData[37] = contentFromBackend.receiver_vat_number;
            modifiedInvoiceData[38] = contentFromBackend.receiver_registry_code;
            modifiedInvoiceData[39] = contentFromBackend.time_table_data;
            modifiedInvoiceData[40] = contentFromBackend.custom_line_items;
            modifiedInvoiceData[41] = contentFromBackend.payment_details;
            modifiedInvoiceData[42] = contentFromBackend.message_top;
            modifiedInvoiceData[43] = contentFromBackend.message_bottom;
            modifiedInvoiceData[44] = contentFromBackend.online_payable;
            modifiedInvoiceData[45] = contentFromBackend.threshold_save;

            // Print invoice
            DownloadDocument(modifiedInvoiceData);
          }
        }
      }
    );
  } else {
    // Print invoice
    DownloadDocument(invoiceData);
  }
}

export { DownloadInvoice };
